// Firma: talep tarih aralığında (veya bugün itibarıyla) süresi dolan onaylı belgeler için uyarı akışı
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { COMPANY_DOCS } from '../../companies/constants';
import { EMPLOYEE_DOCS } from '../../employees/constants';
import { getEquipmentDocs } from './wizardHelpers';
import { findExpiring, latestSafeDate, fmtTr } from './validityCheck';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useValidityPrompt = ({ isFirm, formData, selectedCompany, selectedEmployees, selectedEquipment, setSelectedCompany, setCompanies, setEmployees, setEquipment, setSelectedEquipment, openSection, onClose, deficiencyJumpRef, onAfterReset }) => {
  const [validityPrompt, setValidityPrompt] = useState(null);
  const [validityBusy, setValidityBusy] = useState(false);
  // Firma: talep tarih aralığında (veya bugün itibarıyla) süresi dolan onaylı belgeler için uyarı
  const askValidity = (types, entry = false) => {
    if (!isFirm || !formData.end_date) return false;
    const note = (x) => `geçerlilik: ${fmtTr(x.validity)}${x.expiredNow ? ' (bugün itibarıyla süresi dolmuş)' : ''}`;
    const group = (list, id) => Object.values(list.reduce((acc, x) => ({ ...acc, [id(x)]: [...(acc[id(x)] || []), x] }), {}));
    const labels = (list) => [...new Set(list.map(x => x.label))].join(', ');
    const eqName = (eq) => eq.name || 'Ekipman';
    const items = [], texts = [], sections = [];
    [].concat(types).forEach(type => {
      if (type === 'company') {
        const list = findExpiring(selectedCompany, COMPANY_DOCS, formData.end_date).map(x => ({ ...x, kind: 'company' }));
        if (!list.length) return;
        items.push(...list);
        texts.push(`${labels(list)} firma evraklarınızın süresi dolmuştur.`);
        sections.push({ id: 'company', title: 'Bölüm 2 – Firma Evrakları', lines: list.map(x => `${x.label} – ${note(x)}`) });
      } else if (type === 'employee') {
        const list = selectedEmployees.flatMap(emp => findExpiring(emp, EMPLOYEE_DOCS, formData.end_date).map(x => ({ ...x, emp, kind: 'employee' })));
        if (!list.length) return;
        const byEmp = group(list, x => x.emp.id);
        items.push(...list);
        texts.push(`${byEmp.map(g => `${g[0].emp.name} ${g[0].emp.surname || ''}`.trim()).join(', ')} personelin ${labels(list)} belgelerinin geçerlilik süreleri dolmuştur.`);
        sections.push({ id: 'employee', title: 'Bölüm 3 – Personel Belgeleri', lines: byEmp.map(g => `${g[0].emp.name} ${g[0].emp.surname || ''}: ${g.map(x => `${x.label} (${note(x)})`).join(', ')}`) });
      } else {
        const list = selectedEquipment.flatMap(eq => findExpiring(eq, getEquipmentDocs(eq.equipment_type), formData.end_date, '_date').map(x => ({ ...x, eq, kind: 'equipment' })));
        if (!list.length) return;
        const byEq = group(list, x => x.eq.id);
        items.push(...list);
        texts.push(`${byEq.map(g => eqName(g[0].eq)).join(', ')} iş ekipmanının ${labels(list)} belgelerinin geçerlilik süreleri dolmuştur.`);
        sections.push({ id: 'equipment', title: 'Bölüm 4 – İş Ekipmanı Belgeleri', lines: byEq.map(g => `${eqName(g[0].eq)}: ${g.map(x => `${x.label} (${note(x)})`).join(', ')}`) });
      }
    });
    if (!items.length) return false;
    setValidityPrompt({
      entry, items, texts, sections,
      range: `${fmtTr(formData.start_date)} - ${fmtTr(formData.end_date)}`,
      safeCompany: latestSafeDate([selectedCompany], COMPANY_DOCS),
      safeEmployee: latestSafeDate(selectedEmployees, EMPLOYEE_DOCS),
      safeEquipment: latestSafeDate(selectedEquipment, (eq) => getEquipmentDocs(eq.equipment_type), '_date'),
    });
    return true;
  };

  // X: talep açılışındaki uyarıda listeye döner, akış içinde sadece kapatır
  const closeValidity = () => {
    const entry = validityPrompt?.entry;
    setValidityPrompt(null);
    if (entry) { deficiencyJumpRef.current = null; onClose(); }
  };

  const resetExpiredDocs = async () => {
    setValidityBusy(true);
    try {
      for (const x of validityPrompt.items) {
        const url = {
          company: () => `${API}/companies/${selectedCompany.id}/document-files/${x.key}/reset-expired`,
          employee: () => `${API}/employees/${x.emp.id}/document-files/${x.key}/reset-expired`,
          equipment: () => `${API}/equipment/${x.eq.id}/document-files/${x.key}/reset-expired`,
        }[x.kind]();
        const { data } = await axios.post(url, null, { params: { until: formData.end_date } });
        const apply = (e) => ({ ...e, documents: data.documents, document_files: data.document_files });
        if (x.kind === 'company') {
          setSelectedCompany(prev => apply(prev));
          setCompanies(prev => prev.map(c => (c.id === selectedCompany.id ? apply(c) : c)));
        } else if (x.kind === 'employee') {
          setEmployees(prev => prev.map(e => (e.id === x.emp.id ? apply(e) : e)));
        } else {
          setEquipment(prev => prev.map(e => (e.id === x.eq.id ? apply(e) : e)));
          setSelectedEquipment(prev => prev.map(e => (e.id === x.eq.id ? apply(e) : e)));
        }
      }
      toast.success('Süresi dolan belgeler kaldırıldı. Lütfen güncel belgeleri yükleyin.');
      const kinds = validityPrompt.items.map(x => x.kind);
      setValidityPrompt(null);
      onAfterReset?.(kinds);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Belgeler güncellenemedi');
    } finally { setValidityBusy(false); }
  };

  const goToDates = () => {
    setValidityPrompt(null);
    openSection(1);
    setTimeout(() => {
      document.getElementById('guided-start')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      document.querySelector('[data-testid="wizard-start-date"]')?.focus();
    }, 400);
  };

  return { validityPrompt, validityBusy, askValidity, closeValidity, resetExpiredDocs, goToDates };
};
